import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { encryptMessage, decryptMessage } from "../../utils/crypto";
import "./ChatPage.css";

export default function ChatPage() {
  const location = useLocation();
  const nickName = location.state?.nickName || "Unknown"; // get nickname from unlock page
  const masterPsw = location.state?.masterPsw || ""; // get masterPsw from unlock page
  // Current input message
  const [msg, setMsg] = useState("");

  // Store encrypted/raw messages only
  const [msgs, setMsgs] = useState([]);

  // Store decrypted messages for UI rendering
  const [displayMsgs, setDisplayMsgs] = useState([]);

  const API_BASE = "https://backend.family-chat.workers.dev";

  const loadMessages = async () => {
    try {
      const response = await fetch(`${API_BASE}/messages`);

      if (!response.ok) {
        throw new Error("Failed to load messages");
      }

      const data = await response.json();
      setMsgs(data);
    } catch (error) {
      console.error("Load messages failed:", error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!msg.trim()) return;

    if (!masterPsw) {
      alert("Missing master password.");
      return;
    }

    try {
      const encrypted = await encryptMessage(msg, masterPsw);

      const newMsg = {
        sender: nickName,
        ciphertext: encrypted.ciphertext,
        salt: encrypted.salt,
        iv: encrypted.iv,
        time: new Date().toLocaleTimeString(),
      };

      const response = await fetch(`${API_BASE}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newMsg),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setMsg("");
      await loadMessages();
    } catch (error) {
      console.error("Send failed:", error);
      alert("Failed to send message.");
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadMessages();
      }
    }, 10000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadMessages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    async function buildDisplayMsgs() {
      if (!masterPsw) {
        setDisplayMsgs([]);
        return;
      }

      const normalizedMasterPsw = (masterPsw || "").trim().normalize("NFC");

      const decryptedMsgs = await Promise.all(
        msgs.map(async (item, index) => {
          try {
            const text = await decryptMessage(
              item.ciphertext,
              normalizedMasterPsw,
              item.salt,
              item.iv,
            );

            return {
              id: item.id || index,
              sender: item.sender,
              text,
              time: item.time,
            };
          } catch (error) {
            return {
              id: item.id || index,
              sender: item.sender,
              text: "failed to decrypt",
              time: item.time,
            };
          }
        }),
      );

      setDisplayMsgs(decryptedMsgs);
    }

    buildDisplayMsgs();
  }, [msgs, masterPsw]);

  return (
    <div className="chat-page">
      <div className="chat-page-content">
        <div className="title">Family Chat</div>
        <div className="msg-display">
          {displayMsgs.map((msg) => (
            <div key={msg.id} className="msg-item">
              <div className="msg-content">{msg.text}</div>
              <div className="msg-info">
                <div className="msg-sender">{msg.sender}</div>
                <div className="msg-time">{msg.time}</div>
              </div>
            </div>
          ))}
        </div>
        <form className="chat-box" onSubmit={handleSend}>
          <textarea
            className="chat-box-value"
            placeholder="enter message here"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={4}
          ></textarea>
          <button className="chat-box-btn" type="submit">
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
